import React, { useState, useCallback } from 'react';
import { verifyCertificate, getCertificateRatings, submitCertificateRating, searchLegacyCertificates, downloadExternalCertificate } from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Star, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

const PERIOD_LABELS = {
  c2026: 'Cohorte 2026',
  octubre: 'Octubre',
  noviembre: 'Noviembre',
  otros: 'Otros',
};

const VerifyCertificate = () => {
  const [activeTab, setActiveTab] = useState('code'); // 'code' | 'search'

  // ── Code verification ──────────────────────────────
  const [code, setCode] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [raterName, setRaterName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── Legacy search ──────────────────────────────────
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const LIMIT = 20;

  // ── Code verification handlers ─────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setCertificate(null);
    setReviews([]);
    try {
      const response = await verifyCertificate(code);
      setCertificate(response.data);
      const reviewsRes = await getCertificateRatings(response.data.code);
      setReviews(reviewsRes.data);
    } catch {
      toast.error('Certificado no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!raterName.trim()) {
      toast.error('Por favor ingresa tu nombre');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        certificate_code: certificate.code,
        rater_name: raterName.trim(),
        score: rating,
        comment: comment || null,
      };
      if (email) payload.rater_email = email;
      await submitCertificateRating(payload);
      toast.success('¡Gracias! Tu valoración fue publicada.');
      setShowReviewForm(false);
      setComment(''); setEmail(''); setRaterName(''); setRating(5);
      // refresh list
      const refreshed = await getCertificateRatings(certificate.code);
      setReviews(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al enviar la valoración');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0
    ? reviews.reduce((acc, r) => acc + (r.score || r.rating || 0), 0) / reviews.length : 0;

  // ── Legacy search handlers ─────────────────────────
  const handleSearch = useCallback(async (folio) => {
    setSearching(true);
    setSearched(true);
    try {
      const res = await searchLegacyCertificates(folio.trim(), 1, LIMIT, true);
      setSearchResults(res.data.results);
      setSearchTotal(res.data.total);
    } catch {
      toast.error('Error al buscar certificados');
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Ingresa un número de folio');
      return;
    }
    handleSearch(query);
  };

  return (
    <div className="py-12 bg-secondary min-h-screen" data-testid="verify-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-heading font-bold text-primary mb-4">
            Verificar Certificado
          </h1>
          <p className="text-base text-muted-foreground">
            Verifica la autenticidad de un certificado o consulta un certificado por número de folio
          </p>
        </div>

        {/* Tab selector */}
        <div className="flex mb-6 rounded-lg overflow-hidden border border-border w-fit mx-auto">
          <button
            onClick={() => setActiveTab('code')}
            data-testid="tab-code"
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              activeTab === 'code'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent'
            }`}
          >
            Verificar por Código
          </button>
          <button
            onClick={() => setActiveTab('search')}
            data-testid="tab-search"
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              activeTab === 'search'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent'
            }`}
          >
            Buscar por Folio
          </button>
        </div>

        {/* ── TAB: Code Verification ── */}
        {activeTab === 'code' && (
          <>
            <Card className="mb-8">
              <CardContent className="pt-6">
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Código del Certificado</Label>
                    <Input
                      id="code"
                      placeholder="IAASP-XXXXXXXXXXXX"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      required
                      data-testid="certificate-code-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="verify-btn">
                    {loading ? 'Verificando...' : 'Verificar Certificado'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {certificate && (
              <>
                <Card className="mb-8" data-testid="certificate-result">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Resultado de la Verificación</CardTitle>
                      {certificate.status === 'valid' ? (
                        <Badge className="bg-success text-white">
                          <CheckCircle className="w-4 h-4 mr-1" /> Válido
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="w-4 h-4 mr-1" /> Revocado
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Titular</p>
                        <p className="text-lg font-semibold">{certificate.user_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Curso</p>
                        <p className="text-lg font-semibold">{certificate.course_title}</p>
                      </div>
                      {certificate.hours && (
                        <div>
                          <p className="text-sm text-muted-foreground">Duración</p>
                          <p className="text-lg font-semibold">{certificate.hours} horas</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-muted-foreground">Fecha de Emisión</p>
                        <p className="text-lg font-semibold">
                          {certificate.issued_at ? new Date(certificate.issued_at).toLocaleDateString() : '—'}
                        </p>
                      </div>
                      {certificate.revoked_reason && (
                        <div>
                          <p className="text-sm text-muted-foreground">Motivo de Revocación</p>
                          <p className="text-lg font-semibold text-destructive">{certificate.revoked_reason}</p>
                        </div>
                      )}
                      {/* Download button for external certificates */}
                      {certificate.type === 'external' && certificate.cert_id && (
                        <div className="pt-2">
                          <a
                            href={downloadExternalCertificate(certificate.cert_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="download-external-from-verify"
                          >
                            <Button variant="outline" className="w-full">
                              <Download className="w-4 h-4 mr-2" />
                              Descargar Certificado
                            </Button>
                          </a>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Reviews */}
                <Card className="mb-8">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Valoraciones</CardTitle>
                        <CardDescription>
                          {reviews.length} valoraciones
                          {reviews.length > 0 && ` - Promedio: ${averageRating.toFixed(1)}/5`}
                        </CardDescription>
                      </div>
                      {!showReviewForm && (
                        <Button onClick={() => setShowReviewForm(true)} data-testid="add-review-btn">
                          Añadir Valoración
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {showReviewForm && (
                      <form onSubmit={handleSubmitReview} className="mb-6 p-4 border rounded-lg space-y-4">
                        <div className="space-y-2">
                          <Label>Calificación</Label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button key={star} type="button" onClick={() => setRating(star)} className="focus:outline-none">
                                <Star className={`w-8 h-8 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="rater-name">Tu Nombre</Label>
                          <Input id="rater-name" placeholder="Nombre completo" value={raterName} onChange={(e) => setRaterName(e.target.value)} required data-testid="rating-name-input" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="comment">Comentario (opcional)</Label>
                          <Textarea id="comment" placeholder="Escribe tu comentario..." value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="rating-comment-input" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Tu Email (opcional)</Label>
                          <Input id="email" type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="rating-email-input" />
                        </div>
                        <div className="flex gap-2">
                          <Button type="submit" disabled={submitting} data-testid="submit-review-btn">
                            {submitting ? 'Enviando...' : 'Enviar Valoración'}
                          </Button>
                          <Button type="button" variant="outline" onClick={() => setShowReviewForm(false)}>Cancelar</Button>
                        </div>
                      </form>
                    )}
                    <div className="space-y-4">
                      {reviews.length === 0 ? (
                        <p className="text-center text-muted-foreground py-4">No hay valoraciones aún</p>
                      ) : (
                        reviews.map((review) => (
                          <div key={review.id} className="p-4 border rounded-lg" data-testid={`review-${review.id}`}>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star key={star} className={`w-4 h-4 ${star <= (review.score || review.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                              ))}
                              {review.rater_name && <span className="text-sm font-medium">{review.rater_name}</span>}
                              <span className="text-xs text-muted-foreground">· {new Date(review.created_at).toLocaleDateString()}</span>
                            </div>
                            {review.comment && <p className="text-sm">{review.comment}</p>}
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* ── TAB: Legacy Search ── */}
        {activeTab === 'search' && (
          <>
            <Card className="mb-6">
              <CardContent className="pt-6">
                <form onSubmit={handleSearchSubmit} className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Ingresa el número de folio (ej: 112101)..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="pl-10"
                      data-testid="legacy-search-input"
                    />
                  </div>
                  <Button type="submit" disabled={searching} data-testid="legacy-search-btn">
                    {searching ? 'Buscando...' : 'Buscar por Folio'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Results list */}
            {searchResults.length > 0 && (
              <div className="space-y-3 mb-6" data-testid="legacy-results">
                {searchResults.map((cert) => (
                  <Card key={cert.id} data-testid={`legacy-cert-${cert.folio}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center shrink-0">
                            <CheckCircle className="w-5 h-5 text-success" />
                          </div>
                          <div>
                            <p className="font-semibold">{cert.student_name}</p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              Folio: <span className="font-mono font-medium">{cert.folio}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-success text-white">Aprobado</Badge>
                          <Badge variant="outline" className="text-xs">
                            {PERIOD_LABELS[cert.period] || cert.period}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {searched && searchResults.length === 0 && !searching && (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-results">
                <XCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No se encontró ningún certificado</p>
                <p className="text-sm mt-1">Verifica que el número de folio sea correcto</p>
              </div>
            )}

            {!searched && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Ingresa el número de folio para buscar</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyCertificate;
